import React, { useEffect, useRef, useState } from 'react'
import { Button, Spinner } from 'reactstrap'
import { IMPORTS, MOUNT_META } from './demoRegistry'
import DemoErrorBoundary from './DemoErrorBoundary'
import LayoutPreview from './LayoutPreview'

const noop = () => {}

const SAMPLE_ROWS = [
  { id: 1, nome: 'Placeholder A', status: 'Ativo', diaRow: 2, propItemGr_desc: 'Grupo', propOrcItem_valor: 100, propItem_desc: 'Item' },
  { id: 2, nome: 'Placeholder B', status: 'Pendente', diaRow: 5, propItemGr_desc: 'Grupo', propOrcItem_valor: 50, propItem_desc: 'Item 2' },
]

function buildStubProps(meta, { open, setOpen }) {
  const close = () => setOpen(false)
  const produto = {
    prod_id: 1,
    prod_desc: 'Produto placeholder',
    prod_responsavel: 1,
    prodStatusActual_id: 2,
  }

  return {
    isOpen: open,
    openModal: open,
    openModalErro: open,
    open: open,
    isModalOpen: open,
    addFavModal: open,
    modalIsOpen: open,
    toggle: close,
    onClose: close,
    onClosed: close,
    onConfirm: close,
    onSuccess: close,
    onSecondary: close,
    setOpenModalErro: setOpen,
    title: `Placeholder — ${meta.nome}`,
    subtitle: 'Texto placeholder',
    text: 'Conteúdo placeholder (não é dado de produção).',
    size: 'lg',
    tabela: 'demo',
    data: [],
    confirmButtonText: 'Ok',
    cancelButtonText: 'Fechar',
    pf_id: 1,
    userOikosId: 1,
    setCount: noop,
    setRows: noop,
    rows: SAMPLE_ROWS,
    datesObj: { start: '2026-08-01', end: '2026-08-10' },
    days: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
    produto,
    projectProducts: [produto],
    dataContratual: {},
    tecResp: { pf_id: 1, pf_nomeExibicao: 'Nome placeholder' },
    actual: 2,
    item: { step: 2, label: 'Placeholder' },
    dataPrevista: '2026-08-20',
    orcRubr_id: 1,
    onProductTimelineChanged: noop,
    setContrFornData: noop,
    contrFornData: { contrForn_parteTab: 'pj', contrForn_parteId: 1 },
    isPf: false,
    standalone: true,
    children: <div className="p-3">Children placeholder ({meta.nome})</div>,
    enabled: true,
    objLegenda: { Ativo: '#339933', Pendente: '#f0ad4e' },
    label: 'Placeholder',
    clickMessage: '[UiPlayground] placeholder',
    compraCotacoes: [],
    isLoading: false,
    linhas: SAMPLE_ROWS,
    linhasGastos: [],
    linhasIncome: [],
    chaveCabecalhoLinhas: 'propItem_desc',
    chaveValor: 'propOrcItem_valor',
    chaveIdentificador: 'id',
    quantidadeColunas: 3,
    setQuantidadeColunas: noop,
    valoresPorLinhaEColuna: {},
    setValoresPorLinhaEColuna: noop,
    handleChangeQuantidadeColunas: noop,
    valorTotal: 150,
    series: [
      { name: 'A', data: [{ x: '2026-01', y: 10 }, { x: '2026-02', y: 20 }] },
    ],
    minutaModId: null,
    pedidoCompraData: {},
    options: [
      { value: 'a', label: 'Opção A (placeholder)' },
      { value: 'b', label: 'Opção B (placeholder)' },
    ],
    value: null,
    onChange: noop,
    loadOptions: () => Promise.resolve([{ value: 'a', label: 'Opção A (placeholder)' }]),
    defaultOptions: true,
    mask: '000.000.000-00',
    placeholder: 'placeholder',
    checked: false,
    breadcrumbItems: [{ title: 'Placeholder' }],
    theme: 'light',
    isMenuOpened: false,
    openLeftMenuCallBack: noop,
    menuOpen: false,
    count: 1,
    icon: () => null,
    tone: 'secondary',
    position: 'top',
  }
}

function UtilPanel({ mod, meta }) {
  const keys = Object.keys(mod).filter((k) => k !== 'default' && !k.startsWith('__'))
  return (
    <div className="ui-pg-util">
      <p className="mb-2">
        Módulo real carregado: <code>{meta.arquivo}</code> (sem UI própria).
      </p>
      <ul className="mb-0">
        {keys.map((k) => (
          <li key={k}>
            <code>{k}</code>
            <span className="text-muted"> — {typeof mod[k]}</span>
          </li>
        ))}
        {!keys.length && mod.default && (
          <li><code>default</code> — {typeof mod.default}</li>
        )}
      </ul>
    </div>
  )
}

function resolveComponent(mod) {
  if (mod && mod.default) return mod.default
  if (!mod) return null
  const fns = Object.entries(mod).filter(([, v]) => typeof v === 'function')
  if (fns.length) return fns[0][1]
  return null
}

/**
 * Tenta montar o componente React real com props placeholder.
 * Falha de import → silhueta. Crash de render → DemoErrorBoundary → silhueta.
 */
export default function ComponentMount({ item, forceLoad }) {
  const meta = MOUNT_META[item.codigo] || {
    mode: 'inline',
    openProp: 'isOpen',
    nome: item.nome,
    arquivo: item.arquivo,
  }
  const hostRef = useRef(null)
  const [visible, setVisible] = useState(Boolean(forceLoad))
  const [mod, setMod] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (forceLoad) setVisible(true)
  }, [forceLoad])

  useEffect(() => {
    if (visible || forceLoad) return undefined
    const el = hostRef.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      setVisible(true)
      return undefined
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true)
          io.disconnect()
        }
      },
      { rootMargin: '200px 0px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [visible, forceLoad])

  useEffect(() => {
    if (!visible) return undefined
    let cancelled = false
    const loader = IMPORTS[item.codigo]
    if (!loader) {
      setLoadError(new Error('Sem import para ' + item.codigo))
      return undefined
    }
    setLoading(true)
    loader()
      .then((m) => {
        if (!cancelled) {
          setMod(m)
          setLoadError(null)
        }
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [visible, item.codigo])

  const stub = buildStubProps(meta, { open, setOpen })

  let body = null
  if (visible && !loading && loadError) {
    body = (
      <div className="ui-pg-mount-failed">
        <p className="ui-pg-mount-failed__msg">
          Import falhou — silhueta.
          <code className="d-block mt-1">{String(loadError.message || loadError)}</code>
        </p>
        <LayoutPreview item={item} />
      </div>
    )
  } else if (visible && !loading && mod) {
    if (meta.mode === 'util' || meta.mode === 'reexport') {
      body = <UtilPanel mod={mod} meta={meta} />
    } else {
      const Comp = resolveComponent(mod)
      if (!Comp) {
        body = (
          <div className="ui-pg-mount-failed">
            <p className="ui-pg-mount-failed__msg">Sem export React — silhueta.</p>
            <LayoutPreview item={item} />
          </div>
        )
      } else if (meta.mode === 'modal') {
        body = (
          <div className="ui-pg-mount-box">
            <Button color="primary" size="sm" className="mb-2" onClick={() => setOpen(true)}>
              Abrir {meta.nome}
            </Button>
            <Comp {...stub} />
          </div>
        )
      } else if (meta.mode === 'self') {
        body = (
          <div className="ui-pg-mount-box">
            <p className="small text-muted mb-2">
              Trigger do próprio componente (texto/dados placeholder).
            </p>
            <Comp {...stub} />
          </div>
        )
      } else if (meta.mode === 'shell') {
        body = (
          <div className="ui-pg-shell-frame">
            <Comp {...stub}>
              <div className="p-3 bg-light border">Children placeholder</div>
            </Comp>
          </div>
        )
      } else {
        body = (
          <div className="ui-pg-mount-box">
            <Comp {...stub} />
          </div>
        )
      }
    }
  }

  return (
    <div ref={hostRef} className="ui-pg-mount">
      {!visible && (
        <div className="ui-pg-mount-placeholder">Role até aqui para carregar o componente…</div>
      )}
      {visible && loading && (
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <Spinner size="sm" />
          <span className="small text-muted">Importando {meta.arquivo}…</span>
        </div>
      )}
      {body && (
        <DemoErrorBoundary codigo={item.codigo} item={item} key={`${item.codigo}-${open}`}>
          {body}
        </DemoErrorBoundary>
      )}
    </div>
  )
}
